import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Trash2, Mail, User, Clock } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function Settings() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [confirmText, setConfirmText] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [showDanger, setShowDanger] = useState(false);
    const [digestOptOut, setDigestOptOut] = useState(!!user?.email_digest_opt_out);
    const [checkinEnabled, setCheckinEnabled] = useState(!!user?.checkin_enabled);
    const [checkinHour, setCheckinHour] = useState(user?.checkin_hour ?? 20);

    const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "am" : "pm"}`;

    const saveReminder = async (enabled, hour) => {
        try {
            await api.put("/users/me/preferences", {
                checkin_enabled: enabled,
                checkin_hour: hour,
                tz_offset_minutes: -new Date().getTimezoneOffset(),
            });
            toast.success(enabled ? `pax will check in around ${hourLabel(hour)}` : "check-in reminders off");
        } catch {
            toast.error("Couldn't save that");
        }
    };

    const toggleCheckin = () => {
        const next = !checkinEnabled;
        setCheckinEnabled(next);
        saveReminder(next, checkinHour);
    };

    const changeHour = (e) => {
        const h = parseInt(e.target.value, 10);
        setCheckinHour(h);
        if (checkinEnabled) saveReminder(true, h);
    };

    const toggleDigest = async () => {
        const next = !digestOptOut;
        setDigestOptOut(next);
        try {
            await api.put("/users/me/preferences", { email_digest_opt_out: next });
            toast.success(next ? "Email check-ins paused" : "Email check-ins on");
        } catch {
            setDigestOptOut(!next);
            toast.error("Couldn't save that");
        }
    };

    const deleteAccount = async () => {
        if (confirmText !== "DELETE") return;
        setDeleting(true);
        try {
            await api.post("/account/delete", { confirm: "DELETE" });
            toast.success("Your account and all data have been deleted.");
            logout();
            navigate("/");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete account");
            setDeleting(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto pt-4 md:pt-8" data-testid="settings-page">
                <h1 className="font-heading text-3xl md:text-4xl text-ink mb-8">settings</h1>

                <section className="bg-white/70 border border-clayHover rounded-3xl p-6 md:p-8 shadow-soft mb-6">
                    <div className="flex items-center gap-2 text-inkSoft text-sm mb-4 font-body">
                        <User className="w-4 h-4" /> account
                    </div>
                    <div className="space-y-2 font-body text-ink">
                        <p data-testid="settings-name"><span className="text-inkSoft">name:</span> {user?.preferred_name || user?.name}</p>
                        <p data-testid="settings-email"><span className="text-inkSoft">email:</span> {user?.email}</p>
                    </div>
                </section>

                <section className="bg-white/70 border border-clayHover rounded-3xl p-6 md:p-8 shadow-soft mb-6">
                    <div className="flex items-center gap-2 text-inkSoft text-sm mb-4 font-body">
                        <Mail className="w-4 h-4" /> email check-ins
                    </div>
                    <label className="flex items-center justify-between gap-4 font-body text-ink cursor-pointer">
                        <span className="text-sm leading-relaxed">
                            Get a gentle email when you've been away a while. Never spammy — at most one a day.
                        </span>
                        <button
                            data-testid="digest-toggle"
                            onClick={toggleDigest}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${digestOptOut ? "bg-clayHover" : "bg-terracotta"}`}
                            aria-label="Toggle email check-ins"
                        >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${digestOptOut ? "left-0.5" : "left-[22px]"}`} />
                        </button>
                    </label>
                </section>

                <section className="bg-white/70 border border-clayHover rounded-3xl p-6 md:p-8 shadow-soft mb-6">
                    <div className="flex items-center gap-2 text-inkSoft text-sm mb-4 font-body">
                        <Clock className="w-4 h-4" /> daily check-in time
                    </div>
                    <div className="flex items-center justify-between gap-4 font-body text-ink mb-4">
                        <span className="text-sm leading-relaxed">
                            Pick a moment that's yours — pax will gently nudge you (notification + email) at that time each day.
                        </span>
                        <button
                            data-testid="checkin-toggle"
                            onClick={toggleCheckin}
                            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${checkinEnabled ? "bg-terracotta" : "bg-clayHover"}`}
                            aria-label="Toggle daily check-in reminder"
                        >
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checkinEnabled ? "left-[22px]" : "left-0.5"}`} />
                        </button>
                    </div>
                    {checkinEnabled && (
                        <div className="flex items-center gap-3 font-body text-sm text-ink">
                            <span className="text-inkSoft">remind me around</span>
                            <select
                                data-testid="checkin-hour-select"
                                value={checkinHour}
                                onChange={changeHour}
                                className="bg-clay/50 border border-clayHover rounded-xl px-3 py-2 outline-none focus:border-terracotta"
                            >
                                {Array.from({ length: 24 }, (_, h) => (
                                    <option key={h} value={h}>{hourLabel(h)}</option>
                                ))}
                            </select>
                            <span className="text-inkSoft text-xs">your local time</span>
                        </div>
                    )}
                </section>

                <section className="bg-white/70 border border-red-200 rounded-3xl p-6 md:p-8 shadow-soft">
                    <div className="flex items-center gap-2 text-red-500 text-sm mb-4 font-body">
                        <AlertTriangle className="w-4 h-4" /> danger zone
                    </div>
                    {!showDanger ? (
                        <button
                            data-testid="show-delete-account"
                            onClick={() => setShowDanger(true)}
                            className="text-sm font-body text-red-500 hover:text-red-600 flex items-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" /> delete my account
                        </button>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm font-body text-inkSoft leading-relaxed">
                                This permanently deletes your account, conversations, journals, memories and
                                everything Pax knows about you. This cannot be undone.
                            </p>
                            <input
                                data-testid="delete-confirm-input"
                                value={confirmText}
                                onChange={e => setConfirmText(e.target.value)}
                                placeholder='type DELETE to confirm'
                                className="w-full bg-clay/50 border border-clayHover rounded-xl px-4 py-3 font-body text-ink outline-none focus:border-red-300"
                            />
                            <div className="flex gap-3">
                                <button
                                    data-testid="delete-account-btn"
                                    onClick={deleteAccount}
                                    disabled={confirmText !== "DELETE" || deleting}
                                    className="px-5 py-2.5 rounded-full bg-red-500 text-white font-body text-sm disabled:opacity-40 hover:bg-red-600 transition-colors"
                                >
                                    {deleting ? "deleting…" : "permanently delete"}
                                </button>
                                <button
                                    onClick={() => { setShowDanger(false); setConfirmText(""); }}
                                    className="px-5 py-2.5 rounded-full bg-clay text-ink font-body text-sm hover:bg-clayHover transition-colors"
                                >
                                    cancel
                                </button>
                            </div>
                        </div>
                    )}
                </section>
            </div>
    );
}
